import { resources } from '@/i18n/resources'
import { articleMedia } from './articleMedia'
import { SITE_URL } from './sitePaths'

export const legacyPageSeo: Record<string, { title: string; description: string }> = {
  '/products/dextro-iot-core': resources.en.translation.dextroIotCore.meta,
  '/mission/new-era/church': {
    title: 'New Horizon | Preparing Humanity for the New Era',
    description: resources.en.translation.nhc.hero.description,
  },
  '/secrets/maconaria': {
    title: 'To the G∴A∴O∴T∴U∴ | Marcos Aquino',
    description: 'A personal reflection on science, family and the pursuit of truth and light.',
  },
}

export function getPageSeo(pathname: string) {
  const locale = 'en-US'
  const copy = resources.en.translation.portfolio
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '')
  const article = copy.articles.find((item) => path === `/writing/${item.slug}`)
  const home = path === '/'
  const legacy = legacyPageSeo[path]
  const found = home || Boolean(article) || Boolean(legacy)
  const canonical = found ? SITE_URL + path : undefined
  const author = {
    '@type': 'Person',
    '@id': `${SITE_URL}/#marcos-aquino`,
    name: 'Marcos Aquino',
    url: SITE_URL + '/#about',
    image: `${SITE_URL}/images/marcos-aquino.jpg`,
    jobTitle: 'Embedded systems engineer',
    knowsAbout: ['Embedded systems', 'Firmware', 'Hardware design', 'Industrial automation', 'IoT', 'Dextro Cloud'],
    sameAs: ['https://linkedin.com/in/marcos-aquino', 'https://github.com/marcos-aquino'],
  }
  const title = article ? `${article.title} | Marcos Aquino` : legacy?.title ?? (home ? copy.meta.title : `${copy.writing.notFound} | Marcos Aquino`)
  const description = article?.summary ?? legacy?.description ?? (home ? copy.meta.description : copy.writing.notFoundDescription)
  const image = SITE_URL + (article ? articleMedia[article.slug]?.src : '/images/marcos-aquino.jpg')
  const schema = found ? {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, name: 'Marcos Aquino', url: `${SITE_URL}/`, inLanguage: 'en-US' },
      author,
      article ? {
        '@type': 'Article', '@id': `${canonical}#article`, url: canonical,
        headline: article.title, description, image, inLanguage: locale,
        articleSection: article.category,
        author: { '@type': 'Person', name: author.name, url: author.url, '@id': author['@id'] },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      } : {
        '@type': home ? 'ProfilePage' : 'WebPage', '@id': canonical, url: canonical,
        name: title, description, inLanguage: locale,
        ...(home ? { mainEntity: { '@id': author['@id'] } } : {}),
      },
    ],
  } : undefined
  return { locale, title, description, canonical, image, schema, type: article ? 'article' : 'website',
    robots: found ? 'index, follow, max-image-preview:large' : 'noindex, follow',

  }
}

export type PageSeo = ReturnType<typeof getPageSeo>

const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
export const serializeSchema = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c')

export function renderSeoHead(seo: PageSeo): string {
  const meta = (key: string, content: string, property = false) => `<meta data-seo ${property ? 'property' : 'name'}="${key}" content="${escapeHtml(content)}" />`
  return [
    `<title>${escapeHtml(seo.title)}</title>`,
    meta('description', seo.description), meta('robots', seo.robots), meta('author', 'Marcos Aquino'),
    meta('og:title', seo.title, true), meta('og:description', seo.description, true),
    meta('og:type', seo.type, true), meta('og:site_name', 'Marcos Aquino', true),
    meta('og:locale', seo.locale.replace('-', '_'), true),
    meta('og:image', seo.image, true), meta('og:image:alt', seo.title, true),
    meta('twitter:card', 'summary_large_image'), meta('twitter:title', seo.title),
    meta('twitter:description', seo.description), meta('twitter:image', seo.image),
    ...(seo.canonical ? [meta('og:url', seo.canonical, true), `<link data-seo rel="canonical" href="${seo.canonical}" />`] : []),
    ...(seo.schema ? [`<script data-seo type="application/ld+json">${serializeSchema(seo.schema)}</script>`] : []),
  ].join('\n')
}
